import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Typography, Button, message, Row, Col, Statistic, Space, Table } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, LeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../../shared/api/AllApi';
import { formatCurrency } from '../../../utils/formatters';
import { formatOrderNo } from '../../../shared/lib/documentNumbers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Title } = Typography;

const CustomerOrderDetail = () => {
  const { customerId, orderId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [totalWeightOrder, settotalWeightOrder] = useState(0);
  const [currentOrderIndex, setCurrentOrderIndex] = useState(-1);

  const fetchCustomerOrders = useCallback(async () => {
    try {
      const response = await apiClient.get(`/customers/${customerId}/orders`);      
      setCustomerOrders(response.data.orders);
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      message.error(t('fetchError', 'Error loading data'));
    }
  }, [customerId, t]);

  const fetchOrderDetails = useCallback(async (targetOrderId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/orders/${targetOrderId}`);
      setOrder(response.data);
      settotalWeightOrder(response.data.total_weight || 0); 
      setLoading(false);
    } catch (error) {
      console.error('Error fetching order details:', error);
      message.error(t('fetchError', 'Error loading data'));
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCustomerOrders();
  }, [fetchCustomerOrders]);

  useEffect(() => {
    if (customerOrders.length > 0) {
      const index = customerOrders.findIndex(order => order.id.toString() === orderId);
      setCurrentOrderIndex(index);
      fetchOrderDetails(orderId);
    }
  }, [orderId, customerOrders, fetchOrderDetails]);

  const handleNavigateOrder = (direction) => {
    const newIndex = direction === 'next' ? currentOrderIndex + 1 : currentOrderIndex - 1;
    if (newIndex >= 0 && newIndex < customerOrders.length) {
      navigate(`/customer/${customerId}/order/${customerOrders[newIndex].id}`);
    }
  };

  if (loading || !order) {
    return <div>{t("loading", "Loading…")}</div>;
  }

  // Calculate total order weight from products (after order is loaded)
  const totalOrderWeight = totalWeightOrder;

  return (
    <div style={{ padding: '20px', direction: 'rtl' }}>
      <Card
        style={{
          marginBottom: '20px',
          background: '#f0f2f5',
          borderRadius: '15px',
          padding: '20px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)}>
              {t('back', 'Back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              {order.document_no || formatOrderNo(order.id, order.created_at)}
            </Title>
          </Space>
          <Space>
            <Button 
              type="primary"
              icon={<ArrowRightOutlined />}
              onClick={() => handleNavigateOrder('next')}
              disabled={currentOrderIndex >= customerOrders.length - 1}
            >
              {t('nextOrder', 'Next Order')}
            </Button>
            <Button 
              type="primary"
              icon={<ArrowLeftOutlined />} 
              onClick={() => handleNavigateOrder('prev')}
              disabled={currentOrderIndex <= 0}
            >
              {t('previousOrder', 'Previous Order')}
            </Button>
          </Space>
        </div>
      </Card>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={4}>
          <Card style={{ background: '#e6f7ff', borderRadius: '10px' }}>
            <Statistic
              title={t('totalPrice', 'Total Price')}
              value={formatCurrency(order.total_price)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        {!(order.is_taxed === false || order.is_taxed === 0) && Number(order.commission_amount) > 0 && (
          <Col xs={24} md={4}>
            <Card style={{ background: '#fffbeb', borderRadius: '10px' }}>
              <Statistic
                title={t('posTerminalCommission', 'Commission')}
                value={`+ ${formatCurrency(order.commission_amount)}`}
                valueStyle={{ color: '#d97706' }}
                style={{ textAlign: 'center' }}
              />
            </Card>
          </Col>
        )}
        <Col xs={24} md={4}>
          <Card style={{ background: '#f6ffed', borderRadius: '10px' }}>
            <Statistic
              title={t('totalPaid', 'Total Paid')}
              value={formatCurrency(order.amount_paid)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card style={{ background: '#fff1f0', borderRadius: '10px' }}>
            <Statistic
              title={t('outstandingBalance', 'Outstanding Balance')}
              value={formatCurrency(order.outstanding_balance)}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card style={{ background: '#fffbe6', borderRadius: '10px' }}>
            <Statistic
              title={t('orderWeight', 'Order Weight')}
              value={`${totalOrderWeight} kg`}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={4}>
          <Card style={{ background: '#fff7e6', borderRadius: '10px' }}>
            <Statistic
              title={t('orderDate', 'Order Date')}
              value={(() => {
                if (!order.created_at) return '';
                return dayjs(order.created_at).utc().format('DD/MM/YYYY HH:mm:ss'); // Format as DD/MM/YYYY HH:mm:ss in UTC
              })()}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Order details content will go here */}
      <Card
        style={{
          marginTop: '24px',
          background: '#ffffff',
          borderRadius: '15px',
          padding: '20px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button
            type="primary"
            onClick={() => navigate(`/orders/${order.id}/view`)}
          >
            {t('viewOrderDetails', 'View Full Order Details')}
          </Button>
        </div>
        <Title level={4} style={{ marginBottom: 16 }}>{t('orderedProducts', 'Products Ordered')}</Title>
        <Table
          dataSource={order.order_products || []}
          rowKey={(record, idx) => record.id || idx}
          pagination={false}
          bordered
          columns={[
            {
              title: t('total', 'Total'),
              dataIndex: 'total_price',
              key: 'total',
              render: (total) => {
                return total ? formatCurrency(total) : '-';
              },
            },
            {
              title: t('price', 'Price'),
              dataIndex: 'single_price',
              key: 'price',
              render: (price) => {
                return price ? formatCurrency(price) : '-';
              },
            },
            {
              title: t('totalWeight', 'Total Weight'),
              key: 'totalProductWeight',
              render: (record) => {
                const weight = parseFloat(record.product_weight) || 0;
                const qty = parseFloat(record.quantity) || 0;
                const total = weight * qty;
                return total ? `${total} kg` : '-';
              },
            },
            {
              title: t('weight', 'Weight'),
              dataIndex: 'product_weight',
              key: 'product_weight',
              render: (weight) => weight ? `${weight} kg` : '-',
            },
            {
              title: t('quantity', 'Quantity'),
              dataIndex: 'quantity',
              key: 'quantity',
            },
            {
              title: t('productName', 'Product Name'),
              dataIndex: 'product',
              key: 'productName',
              render: (product) => {
                if (!product) return "N/A";
                let nameObj = {};
                if (typeof product.name === "string") {
                  try {
                    nameObj = JSON.parse(product.name);
                  } catch (e) {
                    nameObj = { en: product.name };
                  }
                } else if (typeof product.name === "object" && product.name !== null) {
                  nameObj = product.name;
                } else {
                  nameObj = { en: "N/A" };
                }
                const { i18n } = require("react-i18next");
                const lang = i18n?.language || "en";
                return (
                  nameObj[lang] ||
                  nameObj.en ||
                  Object.values(nameObj).find(Boolean) ||
                  "N/A"
                );
              },
            },
          ]}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  );
};

export default CustomerOrderDetail;

